import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown } from "lucide-react";
import api from "@/lib/api";

/**
 * Auto-scrolling ticker tape of dividend-paying stocks + ETFs. Sits directly
 * below the site header on the Research page. Each tile is a link to
 * `/research?t=<TICKER>` so a curious visitor can drill in with one tap.
 *
 * Animation: pure CSS `@keyframes marquee` for 60fps on mobile. The list is
 * duplicated inline so the seam never appears — when the first copy scrolls
 * fully off-screen the animation resets to zero and the second copy has
 * already taken its place.
 *
 * Pauses on hover so a user can actually read a symbol before it slides past.
 */
export default function TickerTape() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/research/tape");
        if (!cancelled) setItems(r.data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (items.length === 0) return null;

  // Duplicated for the seamless loop.
  const doubled = [...items, ...items];

  return (
    <div
      className="w-full bg-card border-y border-border overflow-hidden group"
      data-testid="research-ticker-tape"
      aria-label="Live prices for popular dividend tickers"
    >
      <div className="flex whitespace-nowrap animate-marquee group-hover:[animation-play-state:paused]">
        {doubled.map((t, i) => {
          const up = t.change_pct >= 0;
          const Arrow = up ? TrendingUp : TrendingDown;
          return (
            <Link
              key={`${t.ticker}-${i}`}
              to={`/research?t=${t.ticker}`}
              data-testid={`tape-${t.ticker}-${i < items.length ? "a" : "b"}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 border-r border-border/60 hover:bg-muted/40 transition-colors shrink-0"
            >
              <span className="mono text-sm font-semibold tracking-wide">{t.ticker}</span>
              <span className="mono text-sm text-foreground/80">${t.price.toFixed(2)}</span>
              <span className={`mono text-xs inline-flex items-center gap-0.5 ${up ? "text-primary" : "text-destructive"}`}>
                <Arrow className="w-3 h-3" />
                {up ? "+" : ""}{t.change_pct.toFixed(2)}%
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
